import React, { useState } from 'react';
import { X, Users, ShieldCheck, AlertTriangle, RefreshCw, Clock, Trash2, Cloud, Server, Database, Fingerprint, ChevronDown, CheckCircle2, ExternalLink, Loader2, Shield, Building2 } from 'lucide-react';
interface Environment {
  id: string;
  clientName: string;
  type: 'AWS' | 'Azure' | 'GCP' | 'Hybrid' | 'On-Prem';
  status: 'healthy' | 'warning' | 'critical';
  identities: number;
  complianceScore: number;
  overPrivileged: number;
  lastSync: string;
  govCloud?: boolean;
  govCloudRegion?: string;
  cmmcLevel?: number;
  complianceFrameworks?: string[];
}
interface ManageEnvironmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  environment: Environment | null;
}
export function ManageEnvironmentModal({
  isOpen,
  onClose,
  environment
}: ManageEnvironmentModalProps) {
  const [thirdPartyMfaEnabled, setThirdPartyMfaEnabled] = useState(false);
  const [mfaProvider, setMfaProvider] = useState('duo');
  const [mfaTenantId, setMfaTenantId] = useState('');
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(false);
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  const [govCloudEnabled, setGovCloudEnabled] = useState(environment?.govCloud ?? false);
  const [govRegion, setGovRegion] = useState(environment?.govCloudRegion ?? '');
  const [cmmcLevel, setCmmcLevel] = useState(environment?.cmmcLevel ?? 0);
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>(environment?.complianceFrameworks ?? []);
  const allFrameworks = [{
    id: 'nist-171',
    label: 'NIST 800-171',
    description: 'CUI protection requirements'
  }, {
    id: 'cmmc',
    label: 'CMMC',
    description: 'Cybersecurity Maturity Model Certification'
  }, {
    id: 'fedramp-high',
    label: 'FedRAMP High',
    description: 'High-impact federal systems'
  }, {
    id: 'fedramp-mod',
    label: 'FedRAMP Moderate',
    description: 'Moderate-impact federal systems'
  }, {
    id: 'itar',
    label: 'ITAR',
    description: 'International Traffic in Arms Regulations'
  }, {
    id: 'fisma',
    label: 'FISMA',
    description: 'Federal Information Security Management Act'
  }, {
    id: 'nist-53',
    label: 'NIST 800-53',
    description: 'Security & privacy controls'
  }, {
    id: 'dod-srg',
    label: 'DoD SRG',
    description: 'Department of Defense Security Requirements'
  }];
  const govRegionOptions: Record<string, string[]> = {
    Azure: ['USGov Virginia', 'USGov Texas', 'USGov Arizona', 'USDoD Central', 'USDoD East'],
    AWS: ['us-gov-west-1', 'us-gov-east-1'],
    GCP: ['us-gov-west1', 'us-gov-central1'],
    Hybrid: [],
    'On-Prem': []
  };
  const toggleFramework = (label: string) => {
    setSelectedFrameworks(prev => prev.includes(label) ? prev.filter(f => f !== label) : [...prev, label]);
  };
  const mfaProviders = [{
    id: 'duo',
    name: 'Duo Security',
    icon: '🛡️'
  }, {
    id: 'okta',
    name: 'Okta Verify',
    icon: '🔐'
  }, {
    id: 'msauth',
    name: 'Microsoft Authenticator',
    icon: '🪟'
  }, {
    id: 'onelogin',
    name: 'OneLogin',
    icon: '🔑'
  }, {
    id: 'jumpcloud',
    name: 'JumpCloud',
    icon: '☁️'
  }, {
    id: 'custom',
    name: 'Custom Provider',
    icon: '⚙️'
  }];
  const selectedProvider = mfaProviders.find(p => p.id === mfaProvider);
  const handleVerifyConnection = () => {
    if (!mfaTenantId.trim()) return;
    setMfaVerifying(true);
    setMfaVerified(false);
    setTimeout(() => {
      setMfaVerifying(false);
      setMfaVerified(true);
    }, 1800);
  };
  if (!isOpen || !environment) return null;
  const getStatusColor = (status: Environment['status']) => {
    switch (status) {
      case 'healthy':
        return 'text-astro-green';
      case 'warning':
        return 'text-astro-text-secondary';
      case 'critical':
        return 'text-astro-danger';
    }
  };
  const getStatusDot = (status: Environment['status']) => {
    switch (status) {
      case 'healthy':
        return 'bg-astro-green';
      case 'warning':
        return 'bg-astro-text-secondary';
      case 'critical':
        return 'bg-astro-danger';
    }
  };
  const getTypeIcon = (type: Environment['type']) => {
    switch (type) {
      case 'AWS':
      case 'Azure':
      case 'GCP':
        return <Cloud size={14} data-id="element-362" />;
      case 'On-Prem':
        return <Server size={14} data-id="element-363" />;
      case 'Hybrid':
        return <Database size={14} data-id="element-364" />;
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-365">
      <div className="w-full max-w-[560px] max-h-[90vh] bg-astro-card border border-astro-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col" data-id="element-366">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-astro-border shrink-0" data-id="element-367">
          <div className="flex items-center gap-3" data-id="element-368">
            <h2 className="text-lg font-bold text-white" data-id="element-369">
              {environment.clientName}
            </h2>
            <span className="flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-white/5 text-astro-text-secondary border-white/10" data-id="element-370">
              {getTypeIcon(environment.type)}
              {environment.type}
            </span>
          </div>
          <button onClick={onClose} className="text-astro-text-secondary hover:text-white transition-colors p-1" data-id="element-371">

            <X size={20} data-id="element-372" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1 min-h-0" data-id="element-373">
          {/* Status & Stats Overview */}
          <div className="bg-[#0a0e14] border border-astro-border rounded-md p-4" data-id="element-374">
            <div className="flex items-center gap-2 mb-4" data-id="element-375">
              <div className={`w-2 h-2 rounded-full ${getStatusDot(environment.status)} animate-pulse`} data-id="element-376" />

              <span className={`text-xs font-bold uppercase tracking-wider ${getStatusColor(environment.status)}`} data-id="element-377">

                {environment.status}
              </span>
              <span className="text-astro-text-secondary text-xs ml-auto font-mono" data-id="element-378">
                {environment.id}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4" data-id="element-379">
              <div data-id="element-380">
                <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider flex items-center gap-1 mb-1" data-id="element-381">
                  <Users size={10} data-id="element-382" /> Identities
                </span>
                <span className="text-lg font-mono font-bold text-white" data-id="element-383">
                  {environment.identities.toLocaleString()}
                </span>
              </div>
              <div data-id="element-384">
                <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider flex items-center gap-1 mb-1" data-id="element-385">
                  <ShieldCheck size={10} data-id="element-386" /> Compliance
                </span>
                <span className={`text-lg font-mono font-bold ${environment.complianceScore > 85 ? 'text-astro-green' : environment.complianceScore > 70 ? 'text-astro-text-secondary' : 'text-astro-danger'}`} data-id="element-387">

                  {environment.complianceScore}%
                </span>
              </div>
              <div data-id="element-388">
                <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider flex items-center gap-1 mb-1" data-id="element-389">
                  <AlertTriangle size={10} data-id="element-390" /> Over-Privileged
                </span>
                <span className="text-lg font-mono font-bold text-white" data-id="element-391">
                  {environment.overPrivileged}
                </span>
              </div>
            </div>
          </div>

          {/* Scan Configuration */}
          <div data-id="element-392">
            <label className="block text-sm font-medium text-white mb-3 flex items-center gap-2" data-id="element-393">
              <Clock size={16} className="text-astro-green" data-id="element-394" />
              Scan Schedule
            </label>
            <div className="grid grid-cols-3 gap-3" data-id="element-395">
              {['Daily', 'Weekly', 'Monthly'].map(freq => <button key={freq} className={`px-3 py-2 text-sm font-medium rounded border transition-colors ${freq === 'Weekly' ? 'bg-astro-green/10 border-astro-green text-astro-green' : 'bg-[#0a0e14] border-astro-border text-astro-text-secondary hover:text-white hover:border-white/20'}`} data-id="element-396">

                  {freq}
                </button>)}
            </div>
          </div>

          {/* Government Cloud & Compliance Badges */}
          <div data-id="element-397">
            <div className="flex items-center justify-between mb-3" data-id="element-398">
              <label className="text-sm font-medium text-white flex items-center gap-2" data-id="element-399">
                <Building2 size={16} className="text-blue-400" data-id="element-400" />
                Government Cloud & Compliance
              </label>
              <button onClick={() => {
              setGovCloudEnabled(!govCloudEnabled);
              if (govCloudEnabled) {
                setCmmcLevel(0);
                setGovRegion('');
                setSelectedFrameworks([]);
              }
            }} className={`relative w-9 h-5 rounded-full transition-colors ${govCloudEnabled ? 'bg-blue-500' : 'bg-astro-border'}`} data-id="element-401">

                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${govCloudEnabled ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-402" />

              </button>
            </div>

            {!govCloudEnabled && <p className="text-[11px] text-astro-text-secondary leading-relaxed" data-id="element-403">
                Enable to designate this as a Government Cloud environment. This
                activates the
                <span className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-blue-500/10 text-blue-400 border border-blue-500/25" data-id="element-404">
                  <Shield size={8} data-id="element-405" /> GOV
                </span>
                and
                <span className="inline-flex mx-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-astro-green/10 text-astro-green border border-astro-green/30" data-id="element-406">
                  CMMC
                </span>
                badges and applies government-specific compliance scanning.
              </p>}

            {govCloudEnabled && <div className="bg-[#0a0e14] border border-blue-500/15 rounded-md p-4 space-y-4" data-id="element-407">
                {/* Active Badges Preview */}
                <div data-id="element-408">
                  <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider block mb-2" data-id="element-409">
                    Active Badges
                  </span>
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-410">
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-blue-500/10 text-blue-400 border-blue-500/25" data-id="element-411">
                      <Shield size={10} data-id="element-412" />
                      GOV
                    </span>
                    {cmmcLevel > 0 && <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${cmmcLevel >= 3 ? 'text-astro-green bg-astro-green/10 border-astro-green/30' : cmmcLevel >= 2 ? 'text-astro-text-secondary bg-white/5 border-white/10' : 'text-astro-danger bg-astro-danger/10 border-astro-danger/20'}`} data-id="element-413">

                        CMMC L{cmmcLevel}
                      </span>}
                    {selectedFrameworks.map(fw => <span key={fw} className="px-1.5 py-0.5 rounded text-[9px] font-mono font-medium text-astro-text-secondary/70 bg-white/[0.03] border border-white/[0.06]" data-id="element-414">

                        {fw}
                      </span>)}
                    {cmmcLevel === 0 && selectedFrameworks.length === 0 && <span className="text-[11px] text-astro-text-secondary/50 italic" data-id="element-415">
                        Configure level & frameworks below
                      </span>}
                  </div>
                </div>

                {/* Gov Cloud Region */}
                <div data-id="element-416">
                  <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider block mb-1.5" data-id="element-417">
                    Government Region
                  </span>
                  {(govRegionOptions[environment?.type ?? ''] ?? []).length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-418">
                      {(govRegionOptions[environment?.type ?? ''] ?? []).map(region => <button key={region} onClick={() => setGovRegion(region)} className={`px-2.5 py-1.5 text-xs font-mono rounded border transition-colors ${govRegion === region ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' : 'bg-astro-card border-astro-border text-astro-text-secondary hover:text-white hover:border-white/20'}`} data-id="element-419">

                            {region}
                          </button>)}
                    </div> : <input type="text" value={govRegion} onChange={e => setGovRegion(e.target.value)} placeholder="Enter government region..." className="w-full px-3 py-2 bg-astro-card border border-astro-border rounded text-sm text-white placeholder:text-astro-text-secondary/40 focus:outline-none focus:border-blue-500/50 transition-colors font-mono" data-id="element-420" />}
                </div>

                {/* CMMC Level */}
                <div data-id="element-421">
                  <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider block mb-1.5" data-id="element-422">
                    CMMC Maturity Level
                  </span>
                  <div className="flex gap-2" data-id="element-423">
                    {[1, 2, 3, 4, 5].map(level => <button key={level} onClick={() => setCmmcLevel(cmmcLevel === level ? 0 : level)} className={`flex-1 py-2 text-sm font-bold rounded border transition-colors ${cmmcLevel === level ? level >= 3 ? 'bg-astro-green/10 border-astro-green/40 text-astro-green' : level >= 2 ? 'bg-blue-500/10 border-blue-500/30 text-blue-400' : 'bg-astro-danger/10 border-astro-danger/30 text-astro-danger' : 'bg-astro-card border-astro-border text-astro-text-secondary hover:text-white hover:border-white/20'}`} data-id="element-424">

                        L{level}
                      </button>)}
                  </div>
                  <p className="text-[10px] text-astro-text-secondary mt-1.5" data-id="element-425">
                    {cmmcLevel === 0 && 'Select the target CMMC certification level'}
                    {cmmcLevel === 1 && 'Basic cyber hygiene — 17 practices'}
                    {cmmcLevel === 2 && 'Advanced cyber hygiene — 72 practices aligned to NIST 800-171'}
                    {cmmcLevel === 3 && 'Expert practices — 130 practices with enhanced detection'}
                    {cmmcLevel === 4 && 'Proactive — advanced & sophisticated capabilities'}
                    {cmmcLevel === 5 && 'Progressive — optimized & state-of-the-art practices'}
                  </p>
                </div>

                {/* Compliance Frameworks */}
                <div data-id="element-426">
                  <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider block mb-2" data-id="element-427">
                    Compliance Frameworks
                  </span>
                  <div className="grid grid-cols-2 gap-2" data-id="element-428">
                    {allFrameworks.map(fw => {
                  const isSelected = selectedFrameworks.includes(fw.label);
                  return <button key={fw.id} onClick={() => toggleFramework(fw.label)} className={`flex items-start gap-2 px-2.5 py-2 rounded text-left border transition-all ${isSelected ? 'bg-blue-500/8 border-blue-500/25 text-blue-400' : 'bg-transparent border-astro-border text-astro-text-secondary hover:text-white hover:border-white/20'}`} data-id="element-429">

                          <div className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 mt-0.5 ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-astro-border'}`} data-id="element-430">

                            {isSelected && <CheckCircle2 size={8} className="text-white" data-id="element-431" />}
                          </div>
                          <div className="min-w-0" data-id="element-432">
                            <span className="text-[11px] font-bold block" data-id="element-433">
                              {fw.label}
                            </span>
                            <span className="text-[9px] opacity-60 block leading-tight" data-id="element-434">
                              {fw.description}
                            </span>
                          </div>
                        </button>;
                })}
                  </div>
                </div>
              </div>}
          </div>

          {/* Policy Enforcement */}
          <div data-id="element-435">
            <label className="block text-sm font-medium text-white mb-3 flex items-center gap-2" data-id="element-436">
              <ShieldCheck size={16} className="text-astro-green" data-id="element-437" />
              Policy Enforcement
            </label>
            <div className="bg-[#0a0e14] border border-astro-border rounded-md p-4 space-y-3" data-id="element-438">
              {[{
              label: 'Auto-revoke over-privileged access',
              checked: false
            }, {
              label: 'Enforce MFA for all identities',
              checked: true
            }, {
              label: 'Block unused accounts after 30 days',
              checked: true
            }].map(policy => <label key={policy.label} className="flex items-center gap-3 cursor-pointer group" data-id="element-439">

                  <input type="checkbox" defaultChecked={policy.checked} className="w-4 h-4 rounded border-astro-border bg-[#0a0e14] text-astro-green focus:ring-astro-green focus:ring-offset-0" data-id="element-440" />

                  <span className="text-sm text-astro-text-secondary group-hover:text-white transition-colors" data-id="element-441">
                    {policy.label}
                  </span>
                </label>)}
            </div>
          </div>

          {/* Third-Party MFA Integration */}
          <div data-id="element-442">
            <div className="flex items-center justify-between mb-3" data-id="element-443">
              <label className="text-sm font-medium text-white flex items-center gap-2" data-id="element-444">
                <Fingerprint size={16} className="text-astro-green" data-id="element-445" />
                Third-Party MFA Tracking
              </label>
              <button onClick={() => {
              setThirdPartyMfaEnabled(!thirdPartyMfaEnabled);
              if (thirdPartyMfaEnabled) {
                setMfaVerified(false);
                setMfaTenantId('');
              }
            }} className={`relative w-9 h-5 rounded-full transition-colors ${thirdPartyMfaEnabled ? 'bg-astro-green' : 'bg-astro-border'}`} data-id="element-446">

                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${thirdPartyMfaEnabled ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-447" />

              </button>
            </div>

            {!thirdPartyMfaEnabled && <p className="text-[11px] text-astro-text-secondary leading-relaxed" data-id="element-448">
                Enable this if MFA is managed by an external provider.
                Missing-MFA findings will be cross-referenced against the
                third-party enrollment data.
              </p>}

            {thirdPartyMfaEnabled && <div className="bg-[#0a0e14] border border-astro-border rounded-md p-4 space-y-4" data-id="element-449">
                {/* Provider Selector */}
                <div data-id="element-450">
                  <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider block mb-1.5" data-id="element-451">
                    MFA Provider
                  </span>
                  <div className="relative" data-id="element-452">
                    <button onClick={() => setShowProviderDropdown(!showProviderDropdown)} className="w-full flex items-center justify-between px-3 py-2 bg-astro-card border border-astro-border rounded text-sm text-white hover:border-white/20 transition-colors" data-id="element-453">

                      <span className="flex items-center gap-2" data-id="element-454">
                        <span data-id="element-455">{selectedProvider?.icon}</span>
                        <span data-id="element-456">{selectedProvider?.name}</span>
                      </span>
                      <ChevronDown size={14} className={`text-astro-text-secondary transition-transform ${showProviderDropdown ? 'rotate-180' : ''}`} data-id="element-457" />

                    </button>
                    {showProviderDropdown && <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-astro-card border border-astro-border rounded-md shadow-xl overflow-hidden" data-id="element-458">
                        {mfaProviders.map(provider => <button key={provider.id} onClick={() => {
                    setMfaProvider(provider.id);
                    setShowProviderDropdown(false);
                    setMfaVerified(false);
                  }} className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${provider.id === mfaProvider ? 'bg-astro-green/10 text-astro-green' : 'text-astro-text-secondary hover:text-white hover:bg-white/[0.03]'}`} data-id="element-459">

                            <span data-id="element-460">{provider.icon}</span>
                            <span data-id="element-461">{provider.name}</span>
                          </button>)}
                      </div>}
                  </div>
                </div>

                {/* Tenant / Integration ID */}
                <div data-id="element-462">
                  <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider block mb-1.5" data-id="element-463">
                    {mfaProvider === 'duo' ? 'Duo Integration Key' : mfaProvider === 'okta' ? 'Okta Org URL' : mfaProvider === 'msauth' ? 'Azure AD Tenant ID' : 'API Identifier'}
                  </span>
                  <div className="flex gap-2" data-id="element-464">
                    <input type="text" value={mfaTenantId} onChange={e => {
                  setMfaTenantId(e.target.value);
                  setMfaVerified(false);
                }} placeholder={mfaProvider === 'duo' ? 'DIXXXXXXXXXXXXXXXXXX' : mfaProvider === 'okta' ? 'https://your-org.okta.com' : mfaProvider === 'msauth' ? 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' : 'Enter identifier...'} className="flex-1 px-3 py-2 bg-astro-card border border-astro-border rounded text-sm text-white placeholder:text-astro-text-secondary/40 focus:outline-none focus:border-astro-green/50 transition-colors font-mono" data-id="element-465" />

                    <button onClick={handleVerifyConnection} disabled={!mfaTenantId.trim() || mfaVerifying} className="px-3 py-2 text-xs font-bold rounded border transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed bg-astro-card border-astro-border text-astro-text-secondary hover:text-white hover:border-white/20" data-id="element-466">

                      {mfaVerifying ? <>
                          <Loader2 size={12} className="animate-spin" data-id="element-467" />
                          Verifying
                        </> : mfaVerified ? <>
                          <CheckCircle2 size={12} className="text-astro-green" data-id="element-468" />

                          Verified
                        </> : <>
                          <ExternalLink size={12} data-id="element-469" />
                          Verify
                        </>}
                    </button>
                  </div>
                </div>

                {/* Connection Status */}
                {mfaVerified && <div className="flex items-start gap-2.5 bg-astro-green/5 border border-astro-green/20 rounded p-3" data-id="element-470">
                    <CheckCircle2 size={14} className="text-astro-green shrink-0 mt-0.5" data-id="element-471" />

                    <div data-id="element-472">
                      <p className="text-xs text-astro-green font-medium" data-id="element-473">
                        {selectedProvider?.name} connected
                      </p>
                      <p className="text-[11px] text-astro-text-secondary mt-0.5 leading-relaxed" data-id="element-474">
                        MFA enrollment data will be synced from{' '}
                        {selectedProvider?.name}. Identities with active{' '}
                        {selectedProvider?.name} enrollment will no longer
                        trigger missing-MFA findings.
                      </p>
                    </div>
                  </div>}

                {/* MFA Coverage Options */}
                <div className="space-y-2.5 pt-1" data-id="element-475">
                  <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider block" data-id="element-476">
                    Coverage Rules
                  </span>
                  {[{
                label: `Suppress missing-MFA alerts for ${selectedProvider?.name}-enrolled users`,
                checked: true
              }, {
                label: 'Flag users missing from both native and third-party MFA',
                checked: true
              }, {
                label: `Require ${selectedProvider?.name} enrollment for privileged accounts`,
                checked: false
              }].map(rule => <label key={rule.label} className="flex items-start gap-3 cursor-pointer group" data-id="element-477">

                      <input type="checkbox" defaultChecked={rule.checked} className="w-4 h-4 rounded border-astro-border bg-[#0a0e14] text-astro-green focus:ring-astro-green focus:ring-offset-0 mt-0.5" data-id="element-478" />

                      <span className="text-[12px] text-astro-text-secondary group-hover:text-white transition-colors leading-relaxed" data-id="element-479">
                        {rule.label}
                      </span>
                    </label>)}
                </div>
              </div>}
          </div>
        </div>

        {/* Sticky Footer Actions */}
        <div className="flex items-center justify-between p-5 border-t border-astro-border bg-astro-card shrink-0" data-id="element-480">
          <div className="flex gap-3" data-id="element-481">
            <button onClick={onClose} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/20 transition-colors" data-id="element-482">

              <RefreshCw size={14} data-id="element-483" />
              Force Sync
            </button>
            <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-astro-danger/70 border border-astro-border rounded hover:text-astro-danger hover:border-astro-danger/30 transition-colors" data-id="element-484">
              <Trash2 size={14} data-id="element-485" />
              Remove
            </button>
          </div>
          <div className="flex gap-3" data-id="element-486">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-487">

              Cancel
            </button>
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-white bg-astro-green-dark hover:bg-astro-green rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)]" data-id="element-488">

              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>;
}